import { selectAuthToken } from 'services/bootup/selectors'
import { selectAuthToken as selectNewAuthToken } from 'services/auth/selectors'
import { createSelector } from 'reselect'
import * as premiumSelectors from 'services/premium/selectors'
import { selectSiteUaStoreSlug } from 'services/site/selectors'
import moment from 'moment'

export const selectUser = (state) => state.user

export const selectUserId = createSelector(
  selectUser,
  (user) => user && user.id,
)

export const selectUsername = createSelector(
  selectUser,
  (user) => user && user.username,
)

export const selectEmail = createSelector(
  selectUser,
  (user) => user && user.email,
)

export const selectGender = createSelector(
  selectUser,
  (user) => user && user.gender,
)

export const selectDisplayName = createSelector(
  selectUser,
  (user) => user && user.display_name,
)

export const selectFirstName = createSelector(
  selectUser,
  (user) => user && user.first_name,
)

export const selectLocation = createSelector(
  selectUser,
  (user) => user && user.location,
)

// analagous to city
export const selectLocality = createSelector(
  selectLocation,
  (location) => location && location.locality,
)

// analagous to state
export const selectRegion = createSelector(
  selectLocation,
  (location) => location && location.region,
)

export const selectCountryCode = createSelector(
  selectLocation,
  (location) => location && location.country,
)

export const selectDisplayLocation = createSelector(
  selectLocation,
  (location) => {
    if (location) {
      const { locality, region, country } = location
      return `${locality}, ${region}, ${country}`
    }

    return 'United States'
  }
)

export const selectTimeZone = (state) => state.user.time_zone

export const selectTimeZoneOffset = createSelector(
  selectUser,
  (user) => user && user.time_zone_offset / 60,
)

export const selectLoggedIn = createSelector(
  selectNewAuthToken,
  selectAuthToken,
  selectUserId,
  (newAuthToken, token, user) => Boolean(newAuthToken && user),
)

export const selectUnitPreference = createSelector(
  selectUser,
  (user) => user && user.display_measurement_system,
)

export const selectPreferredLanguage = createSelector(
  selectUser,
  (user) => user && user.preferred_language,
)

export const selectStoreLink = createSelector(
  selectGender,
  selectLoggedIn,
  selectSiteUaStoreSlug,
  (gender, loggedIn, storeSlug) => {
    const viewerState = loggedIn ? 'logged_in' : 'logged_out'
    let genderPath = ''
    let genderPick = 'top_picks'

    if (gender) {
      genderPick = gender === 'M' ? 'mens_top_picks' : 'women_top_picks'
    }

    if (gender) {
      genderPath = gender === 'M' ? 'mens' : 'womens'
    }

    return `https://www.underarmour.com/en-us/top-gear-picks/mmf/${genderPath}?cid=MMF|REF|${storeSlug}|Site|${genderPick}|${viewerState}`
  }
)

export const selectIsPremium = premiumSelectors.selectIsPremium
export const selectShouldSeeAds = premiumSelectors.selectShouldSeeAds

export const selectDateJoined = createSelector(
  selectUser,
  (user) => user && user.date_joined,
)

export const selectDaysSinceJoinDate = createSelector(
  selectDateJoined,
  (joined) => joined && moment().diff(joined, 'days'),
)

export const selectDateOfBirth = createSelector(
  selectUser,
  (user) => user && user.birthdate,
)

export const selectAge = createSelector(
  selectDateOfBirth,
  (dob) => dob && moment().diff(dob, 'years'),
)

export const selectAgeGroup = createSelector(
  selectAge,
  (age = 0) => {
    if (age < 18) {
      return 'under18'
    } else if (age >= 18 && age <= 24) {
      return '18to24'
    } else if (age >= 25 && age <= 34) {
      return '25to34'
    } else if (age >= 35 && age <= 44) {
      return '35to44'
    } else if (age >= 45 && age <= 54) {
      return '45to54'
    } else if (age >= 55) {
      return '55andOver'
    }
  }
)

export const selectHeight = createSelector(
  selectUser,
  (user) => user && user.height,
)

export const selectWeight = createSelector(
  selectUser,
  (user) => user && user.weight,
)

export const selectBMI = createSelector(
  selectHeight,
  selectWeight,
  (height = 0, weight = 0) => weight / (height * height),
)

export const selectBMITier = createSelector(
  selectBMI,
  (bmi = 0) => {
    const tiers = [
      { min: 40, label: 'Obese Class III', code: 'h' },
      { min: 35, label: 'Obese Class II', code: 'g' },
      { min: 30, label: 'Obese Class I', code: 'f' },
      { min: 25, label: 'Overweight', code: 'e' },
      { min: 18.5, label: 'Normal', code: 'd' },
      { min: 16, label: 'Underweight', code: 'c' },
      { min: 15, label: 'Severely underweight', code: 'b' },
      { min: 0, label: 'Very severely underweight', code: 'a' },
    ]
    return tiers.reduceRight((prev, curr) => {
      return bmi >= curr.min ? curr : prev
    })
  }
)

export const selectBMICode = createSelector(
  selectBMITier,
  (tier) => tier && tier.code,
)

export const selectDFPTargetingAttributes = createSelector(
  selectGender,
  selectAgeGroup,
  selectDaysSinceJoinDate,
  selectLocality,
  selectRegion,
  selectBMICode,
  selectCountryCode,
  (gender, ageGroup, daysSinceJoined, locality, region, bmiCode, countryCode) => {
    return {
      gender: gender === 'M' ? 'male' : 'female',
      ageGroup,
      daysSinceJoined: daysSinceJoined ? daysSinceJoined.toString() : null,
      city: locality,
      state: region,
      bmiCode,
      countryCode,
      url: window.location.pathname,
      ssl: 'y',
    }
  }
)

export const selectProfileImg = createSelector(
  selectUsername,
  (username) => {
    return `/profile/${username}/picture?size=Small`
  }
)

export const selectProfileImgMed = createSelector(
  selectUsername,
  (username) => {
    return `/profile/${username}/picture?size=Medium`
  }
)

export const selectGoalStatement = createSelector(
  selectUser,
  (user) => user && user.goal_statement,
)

export const selectAboutMe = createSelector(
  selectUser,
  (user) => user && user.introduction,
)

export const selectSharePermissions = createSelector(
  selectUser,
  (user) => user && user.sharing
)

export const selectCanShareWithTwitter = createSelector(
  selectSharePermissions,
  (permissions) => permissions && permissions.twitter,
)

export const selectCanShareWithFacebook = createSelector(
  selectSharePermissions,
  (permissions) => permissions && permissions.facebook,
)



// WEBPACK FOOTER //
// ./src/services/user/selectors.js